import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, Signal } from '@angular/core';

import { CypressTagDirective } from '@vlaio/cypress/core';

import { VlaioIconComponent } from '../../icon/icon.component';
import { VlaioButtonAbstractComponent } from '../shared';

/**
 * The `VlaioEventButtonComponent` is a button component that can be used to trigger events.
 * It is a wrapper around the native button element and provides additional functionality
 * such as handling clicks and managing the button's state.
 *
 * It shares the same styles as the `VlaioExternalButtonComponent` and `VlaioInternalButtonComponent`,
 * which makes them visually consistent and easier to use together.
 *
 * If you wish to navigate internally, you should use the `VlaioInternalButtonComponent`.
 * If you wish to navigate externally, you should use the `VlaioExternalButtonComponent`.
 */
@Component({
	selector: 'vlaio-event-button',
	templateUrl: './event-button.component.html',
	styleUrls: ['./event-button.component.scss', '../shared/button-variants.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgClass, CypressTagDirective, VlaioIconComponent]
})
export class VlaioEventButtonComponent extends VlaioButtonAbstractComponent {
	/**
	 * Provide a 'type' for the button.
	 *
	 * This property follows the options and behavior of the spec:
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type
	 */
	public type: Signal<'submit' | 'reset' | 'button'> = input();

	/**
	 * Allow the button to has its content centered or spread (spaced-between). This input
	 * was added to allow the button layout to change without the need for `::ng-deep`. This
	 * will only have an effect if the `fullWidth` is set to `true`.
	 *
	 * Default value is `centered`.
	 *
	 * Note: This cannot be added to the Abstract, because the `layout` is not used in the
	 * external button.
	 */
	public layout: Signal<'centered' | 'spread'> = input('centered');
}
